import { getCookie, setCookie, deleteCookie } from './cookies';

// Shared with the companion static site via static-site-kit's theme.ts.
// Cookie name and value format must stay identical so both sites read/write the same preference.
export type ColorMode = 'auto' | 'light' | 'dark';

const COLOR_MODE_COOKIE = 'theme';
const THEME_EVENT = 'theme-change';
const THEME_CHANNEL = 'theme';
const CYCLE: ColorMode[] = ['auto', 'light', 'dark'];

// broadcasts color mode changes to other tabs on this same origin
const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(THEME_CHANNEL) : null;

function colorModeCookieDomain(apex: string): string | undefined {
  const { hostname } = window.location;
  const onApexDomain = hostname === apex || hostname.endsWith(`.${apex}`);
  return onApexDomain ? apex : undefined;
}

export function getColorMode(): ColorMode {
  const stored = getCookie(COLOR_MODE_COOKIE);
  return stored === 'light' || stored === 'dark' ? stored : 'auto';
}

// `apex` scopes the cookie to that domain (and its subdomains) so this app
// stays in sync with the companion static site's theme choice.
function setColorMode(mode: ColorMode, apex: string): void {
  const domain = colorModeCookieDomain(apex);
  if (mode === 'auto') {
    deleteCookie(COLOR_MODE_COOKIE, { domain });
  } else {
    setCookie(COLOR_MODE_COOKIE, mode, {
      maxAge: 31536000,
      sameSite: 'Lax',
      domain,
      secure: window.location.protocol === 'https:',
    });
  }
}

// persists the mode and notifies same-tab listeners (window event) and other tabs (BroadcastChannel)
export function applyColorMode(mode: ColorMode, apex: string): void {
  setColorMode(mode, apex);
  window.dispatchEvent(new Event(THEME_EVENT)); // notify same-tab
  channel?.postMessage(mode); // notify other tabs
}

export function nextColorMode(mode: ColorMode): ColorMode {
  return CYCLE[(CYCLE.indexOf(mode) + 1) % CYCLE.length];
}

// for useSyncExternalStore — notifies on changes from this tab's toggle or another tab's
export function subscribeColorMode(callback: () => void): () => void {
  window.addEventListener(THEME_EVENT, callback);
  channel?.addEventListener('message', callback);
  return () => {
    window.removeEventListener(THEME_EVENT, callback);
    channel?.removeEventListener('message', callback);
  };
}

export function resolveColorMode(mode: ColorMode, prefersDark: boolean): 'light' | 'dark' {
  return mode === 'auto' ? (prefersDark ? 'dark' : 'light') : mode;
}
